import {doAction} from '../../@shared/ts/hooks';
import {type CloseFlags} from '../../express-checkout-button/ts/sdk-checkout';
import {clearOrderNotices, requestCartCalculation} from './payment/order';
import {startModalLoading, stopModalLoading} from './reducers/environmentReducer';
import {store} from './store';

type CheckoutMessage = {
	type: string;
	requestId?: number;
	useEmbeddedCartCalculationResponse?: boolean;
};

type CheckoutOpenCartCalculationCoordinator = {
	request(message: CheckoutMessage, requestFreshCartCalculation: () => Promise<void>): Promise<void>;
};

function shouldRequestCartCalculationOnCheckoutOpen(message: CheckoutMessage): boolean {
	return message.useEmbeddedCartCalculationResponse !== true;
}

function createCheckoutOpenCartCalculationCoordinator(): CheckoutOpenCartCalculationCoordinator {
	const requests = new Map<number | 'missing-request-id', Promise<void>>();

	return {
		async request(message: CheckoutMessage, requestFreshCartCalculation: () => Promise<void>): Promise<void> {
			if (!shouldRequestCartCalculationOnCheckoutOpen(message)) {
				return;
			}

			const requestKey = message.requestId ?? 'missing-request-id';
			const existingRequest = requests.get(requestKey);
			if (existingRequest) {
				return existingRequest;
			}

			const cartCalculationRequest = requestFreshCartCalculation().finally(() => {
				requests.delete(requestKey);
			});

			requests.set(requestKey, cartCalculationRequest);
			await cartCalculationRequest;
		},
	};
}

const checkoutOpenCartCalculationCoordinator = createCheckoutOpenCartCalculationCoordinator();

function checkoutCloseFlags() {
	const flags: CloseFlags = {};

	return {
		getFlags() {
			return {...flags};
		},
		setRedirect(url: string) {
			flags.redirect = url;
		},
		setReload() {
			flags.reload = true;
		},
		setRefresh() {
			flags.refresh = true;
		},
		resetFlags() {
			delete flags.redirect;
			delete flags.reload;
			delete flags.refresh;
		},
	};
}

const SDKFlags = checkoutCloseFlags();

function initSDKEvents() {
	window.addEventListener('message', async (event: MessageEvent<CheckoutMessage>) => {
		if (event.origin !== location.origin) {
			return;
		}

		if (event.data.type === 'peachpay_checkout_opened') {
			const {requestId} = event.data;
			let modalLoadingStarted = false;

			window.top?.postMessage({type: 'peachpay_checkout_opened_ready', requestId, visualReadySupported: true}, location.origin);

			try {
				// When the express checkout is reopened we want all existing notices to be removed
				clearOrderNotices();

				if (shouldRequestCartCalculationOnCheckoutOpen(event.data)) {
					// We also want the Express checkout to be up to date with the current cart
					modalLoadingStarted = true;
					store.dispatch(startModalLoading());
					await checkoutOpenCartCalculationCoordinator.request(event.data, async () => requestCartCalculation('pull', 'open:pull', {cartCalculationDedupeKey: 'first-open'}));
				}

				await doAction('after_modal_open');
			} finally {
				if (modalLoadingStarted) {
					store.dispatch(stopModalLoading());
				}

				window.top?.postMessage({type: 'peachpay_checkout_visual_ready', requestId}, location.origin);
			}

			return;
		}

		if (event.data.type === 'peachpay_checkout_closed') {
			window.top?.postMessage({type: 'peachpay_checkout_close_flags', data: SDKFlags.getFlags()}, location.origin);

			SDKFlags.resetFlags();
		}
	});
}

function closeCheckout() {
	if (window.top === window) {
		window.location.href = '/cart';
		return;
	}

	window.top?.postMessage({type: 'peachpay_checkout_close'}, location.origin);
}

export {
	initSDKEvents,

	SDKFlags,

	closeCheckout,
	createCheckoutOpenCartCalculationCoordinator,
	shouldRequestCartCalculationOnCheckoutOpen,
};
